import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal/legal-page";
import { SUPPORT_EMAIL } from "@/lib/subiza";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Subiza" },
      {
        name: "description",
        content:
          "How Subiza collects, uses and protects personal information for teachers who create exams and students who take them.",
      },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      effective="9 October 2026"
      intro="This policy explains what personal information Subiza collects, why we collect it, and the choices you have. It covers teachers who create exams and students who take them."
      sections={[
        {
          id: "p1",
          heading: "1. Who this policy covers",
          content: (
            <p>
              This policy applies when you use Subiza as a teacher, when you join an exam as a
              student, or when you simply visit the site. It should be read alongside our Terms and
              Conditions and our Acceptable Use Policy.
            </p>
          ),
        },
        {
          id: "p2",
          heading: "2. What we collect",
          content: (
            <>
              <p>
                <strong>For teachers:</strong> account details (such as your name and email
                address), the exams and questions you create, and any notes or documents you upload
                to generate questions.
              </p>
              <p>
                <strong>For students:</strong> the answers you submit, your score, the timing of
                your exam session, and any name or details you enter when joining.
              </p>
              <p>
                <strong>Automatically:</strong> basic technical information needed to run and secure
                the service, such as device and browser type, and error reports.
              </p>
            </>
          ),
        },
        {
          id: "p3",
          heading: "3. Why we use it",
          content: (
            <ul className="space-y-2.5">
              <li>To create and run exams, and to show results to the teacher who set them.</li>
              <li>To generate questions from the notes and instructions you provide.</li>
              <li>To keep the service secure and to fix problems.</li>
              <li>To respond when you contact us about your account.</li>
            </ul>
          ),
        },
        {
          id: "p4",
          heading: "4. Who helps us run Subiza",
          content: (
            <>
              <p>
                We share the minimum necessary with trusted providers who process data on our
                behalf: Supabase (database, authentication and live updates), Cloudflare (hosting
                and delivery) and Google&rsquo;s Gemini service (drafting questions from your
                notes).
              </p>
              <p>
                We do not sell personal information. We do not use student exam data for
                advertising.
              </p>
            </>
          ),
        },
        {
          id: "p5",
          heading: "5. How long we keep it",
          content: (
            <p>
              We keep exam and result data for as long as the teacher&rsquo;s account is active or
              as needed to provide the service. When an account is deleted, we remove or anonymise
              the data under it, except for limited records we must keep by law.
            </p>
          ),
        },
        {
          id: "p6",
          heading: "6. Your choices and rights",
          content: (
            <p>
              You can ask to see, correct or delete your account and the exams under it. Students
              who want to see or question their results should first ask their teacher, who controls
              the exam. We handle personal data in line with the data protection law that applies in
              Rwanda.
            </p>
          ),
        },
        {
          id: "p7",
          heading: "7. Students and children",
          content: (
            <p>
              Students under 18 should use Subiza only with the knowledge of their teacher or
              school. Teachers and schools are responsible for any permission needed from parents or
              guardians, and should keep students&rsquo; personal information out of notes uploaded
              for question generation.
            </p>
          ),
        },
        {
          id: "p8",
          heading: "8. Security",
          content: (
            <p>
              Keys that connect Subiza to outside services are kept on our servers and are never
              placed in the code your browser downloads. We take reasonable steps to protect your
              data, but no online service is perfectly secure.
            </p>
          ),
        },
        {
          id: "p9",
          heading: "9. Changes to this policy",
          content: (
            <p>
              We may update this policy from time to time. When we do, we change the date and
              version at the top of this page. Significant changes will be shown as a notice in
              Subiza.
            </p>
          ),
        },
        {
          id: "p10",
          heading: "10. Contact",
          content: (
            <p>
              For questions about this policy or to make a data request, contact us at{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-accent underline underline-offset-2 hover:underline-offset-4"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          ),
        },
      ]}
    />
  );
}
